import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PersonStanding, Target, CheckCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function PowerPostureGuide() {
  const navigate = useNavigate();

  const poses = [
    { name: "The Wonder Woman", duration: "2 min", how: "Hands on hips, feet shoulder-width, chin up, chest open", effect: "Increases testosterone, decreases cortisol" },
    { name: "The Victory Pose", duration: "2 min", how: "Arms raised in V shape above head, chin up, big smile", effect: "Celebrates success, boosts mood" },
    { name: "The Executive Lean", duration: "2 min", how: "Lean back, hands behind head, feet up (if possible)", effect: "Projects authority and calm confidence" },
    { name: "The Strong Stance", duration: "2 min", how: "Feet wide, hands on hips or by sides, shoulders back", effect: "Grounded, powerful presence" }
  ];

  const dailyPosture = [
    { situation: "Walking", tip: "Imagine a string pulling you up from the crown of your head" },
    { situation: "Sitting at desk", tip: "Feet flat, shoulders back, screen at eye level" },
    { situation: "In meetings", tip: "Sit at the front, take up space, make eye contact" },
    { situation: "On phone", tip: "Stand up or walk while talking—changes your voice" },
    { situation: "When nervous", tip: "Roll shoulders back, take a deep breath, chin up" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 7 • Day 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Power Posture Day
          </h1>
          <p className="text-xl text-secondary-600">Stand Tall, Feel Powerful</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <PersonStanding className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">The Science</h3>
              <p className="text-purple-100 text-lg">
                Amy Cuddy's research shows that holding power poses for just 2 minutes increases testosterone by 20% 
                and decreases cortisol by 25%. Your body posture changes your brain chemistry.
              </p>
            </div>
          </div>
        </section>

        {/* Power Poses */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Power Poses</h2>
          <div className="space-y-4">
            {poses.map((pose, index) => (
              <div key={index} className="bg-purple-50 rounded-xl p-5">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-purple-700">{pose.name}</h3>
                  <span className="text-sm bg-purple-200 text-purple-700 px-2 py-1 rounded-full">{pose.duration}</span>
                </div>
                <p className="text-secondary-700 mb-2">{pose.how}</p>
                <p className="text-sm text-purple-600 italic">Effect: {pose.effect}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Daily Posture */}
        <section className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 border-2 border-purple-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Everyday Posture Tips</h2>
          <div className="space-y-3">
            {dailyPosture.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 flex items-start gap-3">
                <Target className="w-5 h-5 text-purple-500 mt-1 flex-shrink-0" />
                <div>
                  <span className="font-bold text-purple-700">{item.situation}:</span>
                  <span className="text-secondary-600 ml-2">{item.tip}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default PowerPostureGuide;
